/**
 * 视频最近作品列表
 *
 * 调用 listMyJobs 拉用户的视频任务，显示缩略图（封面）+ 状态。
 * 点击播放，长按删除。
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { RefreshCw, Trash2, Loader2, AlertCircle, Clock } from 'lucide-react';
import { videoApi, type VideoJobUserView, type VideoJobDetail } from '../services/videoApi';
import { withAuthToken } from '../../../utils/storageUrl';
import { getVideoModelById } from '../../../apps/board/modelCache';

/** modelId → 视频模型展示名；同步快照查，未命中时回退 modelId 并触发后台加载 */
function resolveVideoModelName(modelId: string): string {
  return getVideoModelById(modelId)?.name ?? modelId;
}

interface VideoRecentWorksProps {
  /** 父组件可传入「待处理」任务（用于即时刷新） */
  externalRefreshKey?: number;
  /**
   * 点击播放时回调（detail 包含参考图 / 首尾帧的可访问 URL，给"填充"功能用）
   */
  onPlay?: (job: VideoJobUserView, detail: VideoJobDetail) => void;
}

export function VideoRecentWorks({ externalRefreshKey, onPlay }: VideoRecentWorksProps) {
  const [jobs, setJobs] = useState<VideoJobUserView[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const hasLoadedRef = useRef(false);

  const load = useCallback(async () => {
    try {
      setLoading(!hasLoadedRef.current);
      setRefreshing(true);
      const res = await videoApi.listMyJobs({ pageSize: 24 });
      setJobs((prev) => mergeRecentJobs(prev, res.data));
      hasLoadedRef.current = true;
    } catch (err) {
      console.error('[video] 加载列表失败:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load, externalRefreshKey]);

  // 有进行中任务时定时刷新
  useEffect(() => {
    const hasActive = jobs.some((j) =>
      ['pending', 'submitting', 'processing'].includes(j.status)
    );
    if (!hasActive) return;
    const interval = setInterval(load, 8000);
    return () => clearInterval(interval);
  }, [jobs, load]);

  const handleDelete = async (id: string) => {
    if (!confirm('确定隐藏这个视频吗？管理员仍可见。')) return;
    try {
      await videoApi.deleteJob(id);
      setJobs((prev) => prev.filter((j) => j.id !== id));
    } catch (err) {
      alert(err instanceof Error ? err.message : '删除失败');
    }
  };

  const handlePlay = async (job: VideoJobUserView) => {
    // 最近作品里的任何状态都允许装到画布，便于复用提示词 / 参考图 / 参考视频。
    try {
      const detail = await videoApi.getJob(job.id);
      onPlay?.(job, detail);
    } catch (err) {
      console.error('获取视频 URL 失败:', err);
    }
  };

  return (
    <div className="card p-4 mt-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-light-text">最近作品</h3>
        <button
          type="button"
          onClick={load}
          className="flex items-center gap-1 text-xs text-light-muted hover:text-accent-blue"
          disabled={refreshing}
        >
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          刷新
        </button>
      </div>

      {loading ? (
        <div className="py-8 text-center text-light-muted text-sm">加载中...</div>
      ) : jobs.length === 0 ? (
        <div className="py-8 text-center text-light-muted text-sm">还没有作品</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
          {jobs.map((job) => (
            <RecentVideoTile
              key={job.id}
              job={job}
              onPlay={() => handlePlay(job)}
              onDelete={() => handleDelete(job.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface RecentVideoTileProps {
  job: VideoJobUserView;
  onPlay: () => void;
  onDelete: () => void;
}

function RecentVideoTile({ job, onPlay, onDelete }: RecentVideoTileProps) {
  const isActive = ['pending', 'submitting', 'processing'].includes(job.status);
  const isFailed = job.status === 'failed' || job.status === 'timeout';

  return (
    <div className="group relative aspect-video rounded-lg overflow-hidden bg-light-hover border border-light-border">
      <button
        type="button"
        onClick={onPlay}
        className="absolute inset-0 flex items-center justify-center"
      >
        {isActive ? (
          <div className="flex flex-col items-center gap-1 text-light-muted">
            <Loader2 className="w-5 h-5 animate-spin text-accent-blue" />
            <span className="text-xs">{getStatusLabel(job.status)}</span>
          </div>
        ) : isFailed ? (
          <div className="flex flex-col items-center gap-0.5 px-1 text-center text-rose-500">
            <AlertCircle className="w-5 h-5" />
            <span className="text-xs leading-tight">{job.status === 'timeout' ? '超时' : '失败'}</span>
            {job.errorMessage && (
              <span className="line-clamp-2 max-w-[140px] text-[10px] leading-tight text-rose-500/90">
                {job.errorMessage}
              </span>
            )}
            <span className="text-[10px] text-rose-500/80 leading-tight">积分已退回</span>
          </div>
        ) : (
          <CompletedTile job={job} />
        )}
      </button>

      {/* 删除按钮 */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <Trash2 className="w-3 h-3" />
      </button>

      {/* 元信息 */}
      <div className="absolute bottom-0 left-0 right-0 px-2 py-1 bg-gradient-to-t from-black/70 to-transparent">
        <div className="flex items-center gap-1 text-[10px] text-white/90">
          <Clock className="w-3 h-3 shrink-0" />
          <span className="shrink-0">{job.duration}s · {job.resolution}</span>
        </div>
        <div className="truncate text-[10px] text-white/70">{resolveVideoModelName(job.model)}</div>
      </div>
    </div>
  );
}

function CompletedTile({ job }: { job: VideoJobUserView }) {
  const previewCoverUrl = job.coverProxyUrl || job.coverUrl;
  const previewVideoUrl = job.playUrl || job.resultUrl;
  // 优先用上游返回的封面；没有就用视频本身（浏览器渲染首帧）
  if (previewCoverUrl) {
    return (
      <>
        <img
          src={withAuthToken(previewCoverUrl)}
          alt=""
          className="w-full h-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 flex items-center justify-center bg-black/0 hover:bg-black/30 transition-colors">
          <span className="text-xs px-2 py-1 rounded bg-white/20 text-white opacity-0 hover:opacity-100 transition-opacity">
            点击播放
          </span>
        </div>
      </>
    );
  }
  if (previewVideoUrl) {
    return (
      <>
        <video
          src={withAuthToken(previewVideoUrl) + '#t=0.1'}
          preload="metadata"
          muted
          className="w-full h-full object-cover pointer-events-none"
        />
        <div className="absolute inset-0 flex items-center justify-center bg-black/0 hover:bg-black/30 transition-colors">
          <span className="text-xs px-2 py-1 rounded bg-white/20 text-white opacity-0 hover:opacity-100 transition-opacity">
            点击播放
          </span>
        </div>
      </>
    );
  }
  return (
    <div className="w-full h-full flex items-center justify-center bg-black/80 text-white">
      <span className="text-xs px-2 py-1 rounded bg-white/10">点击播放</span>
    </div>
  );
}

function getStatusLabel(status: string): string {
  switch (status) {
    case 'pending':
      return '排队中';
    case 'submitting':
      return '提交中';
    case 'processing':
      return '生成中';
    default:
      return status;
  }
}

function mergeRecentJobs(prev: VideoJobUserView[], next: VideoJobUserView[]): VideoJobUserView[] {
  const prevMap = new Map(prev.map((job) => [job.id, job]));

  return next.map((job) => {
    const previous = prevMap.get(job.id);
    if (!previous) return job;

    const stabilizedJob =
      previous.status === 'completed' && job.status === 'completed'
        ? {
            ...job,
            resultUrl: previous.resultUrl || job.resultUrl,
            coverUrl: previous.coverUrl || job.coverUrl,
            playUrl: previous.playUrl || job.playUrl,
            downloadUrl: previous.downloadUrl || job.downloadUrl,
            coverProxyUrl: previous.coverProxyUrl || job.coverProxyUrl,
          }
        : job;

    if (areRecentJobsEquivalent(previous, stabilizedJob)) {
      return previous;
    }

    return stabilizedJob;
  });
}

function areRecentJobsEquivalent(a: VideoJobUserView, b: VideoJobUserView): boolean {
  return (
    a.id === b.id &&
    a.prompt === b.prompt &&
    a.model === b.model &&
    a.mode === b.mode &&
    a.status === b.status &&
    a.duration === b.duration &&
    a.resolution === b.resolution &&
    a.aspectRatio === b.aspectRatio &&
    a.cameraMovement === b.cameraMovement &&
    a.motionIntensity === b.motionIntensity &&
    a.enableAudio === b.enableAudio &&
    a.creditsUsed === b.creditsUsed &&
    a.resultDuration === b.resultDuration &&
    a.resultWidth === b.resultWidth &&
    a.resultHeight === b.resultHeight &&
    a.resultFileSize === b.resultFileSize &&
    a.resultMimeType === b.resultMimeType &&
    a.errorMessage === b.errorMessage &&
    a.hasError === b.hasError &&
    a.createdAt === b.createdAt &&
    a.updatedAt === b.updatedAt &&
    a.completedAt === b.completedAt &&
    a.resultUrl === b.resultUrl &&
    a.coverUrl === b.coverUrl &&
    a.playUrl === b.playUrl &&
    a.downloadUrl === b.downloadUrl &&
    a.coverProxyUrl === b.coverProxyUrl
  );
}
